import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Plus,
  Trash2,
  Edit,
  Shield,
  ShieldCheck,
  ShieldX,
  UserCog,
  Key,
  History,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Lock,
  Users,
  RefreshCw
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

const PERMISSION_CATEGORIES = {
  'VIEW': { label: 'View Access', color: 'bg-blue-100 text-blue-800' },
  'MANAGE': { label: 'Manage Access', color: 'bg-green-100 text-green-800' },
  'APPROVE': { label: 'Approval Access', color: 'bg-orange-100 text-orange-800' },
  'EXPORT': { label: 'Export Access', color: 'bg-purple-100 text-purple-800' }
};

export default function AdminManagement({ currentUser }) {
  const [admins, setAdmins] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    permissions: []
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const isPrimaryAdmin = currentUser?.is_primary_admin || currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  const fetchData = async () => {
    try {
      const [adminsRes, permsRes, logsRes] = await Promise.all([
        axiosInstance.get('/admin/users'),
        axiosInstance.get('/admin/permissions-list'),
        axiosInstance.get('/admin/audit-logs?limit=50')
      ]);
      setAdmins(adminsRes.data);
      setPermissions(permsRes.data);
      setAuditLogs(logsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      permissions: []
    });
    setEditingAdmin(null);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    if (!editingAdmin && !formData.password) {
      toast.error('Password is required for new admin');
      return;
    }

    try {
      if (editingAdmin) {
        await axiosInstance.patch(`/admin/users/${editingAdmin.id}?current_user_id=${currentUser.id}`, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        });
        toast.success('Admin updated successfully');
      } else {
        await axiosInstance.post(`/admin/users?current_user_id=${currentUser.id}`, formData);
        toast.success('Admin created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save admin');
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Are you sure you want to delete ${admin.name}?`)) return;
    
    try {
      await axiosInstance.delete(`/admin/users/${admin.id}?current_user_id=${currentUser.id}`);
      toast.success('Admin deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const toggleStatus = async (admin) => {
    try {
      const endpoint = admin.status === 'active' 
        ? `/admin/users/${admin.id}/deactivate`
        : `/admin/users/${admin.id}/activate`;
      
      await axiosInstance.post(`${endpoint}?current_user_id=${currentUser.id}`);
      toast.success(`Admin ${admin.status === 'active' ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handlePermissionUpdate = async () => {
    if (!selectedAdmin) return;
    
    try {
      await axiosInstance.patch(
        `/admin/users/${selectedAdmin.id}/permissions?current_user_id=${currentUser.id}`,
        { permissions: formData.permissions }
      );
      toast.success('Permissions updated successfully');
      setPermDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axiosInstance.post(`/auth/change-password?current_user_id=${currentUser.id}`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const openEditDialog = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      phone: admin.phone || '',
      password: '',
      permissions: admin.permissions || []
    });
    setDialogOpen(true);
  };

  const openPermissionsDialog = (admin) => {
    setSelectedAdmin(admin);
    setFormData(prev => ({ ...prev, permissions: admin.permissions || [] }));
    setPermDialogOpen(true);
  };

  const togglePermission = (permValue) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permValue)
        ? prev.permissions.filter(p => p !== permValue)
        : [...prev.permissions, permValue]
    }));
  };

  const selectAllPermissions = () => {
    setFormData(prev => ({
      ...prev,
      permissions: permissions.map(p => p.value)
    }));
  };

  const clearAllPermissions = () => {
    setFormData(prev => ({ ...prev, permissions: [] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTime(dateStr);
  };

  const getActionLabel = (action) => {
    const labels = {
      'admin_created': 'Created Admin',
      'admin_updated': 'Updated Admin',
      'admin_deleted': 'Deleted Admin',
      'admin_activated': 'Activated Admin',
      'admin_deactivated': 'Deactivated Admin',
      'permissions_updated': 'Updated Permissions',
      'password_changed': 'Changed Password'
    };
    return labels[action] || action;
  };

  const groupPermissionsByCategory = () => {
    const grouped = {};
    permissions.forEach(p => {
      const category = p.value.startsWith('view_') ? 'VIEW' 
        : p.value.startsWith('manage_') ? 'MANAGE'
        : p.value.startsWith('approve_') ? 'APPROVE'
        : p.value.startsWith('export_') ? 'EXPORT'
        : 'OTHER';
      
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(p);
    });
    return grouped;
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Management</h1>
          <p className="text-gray-500">Manage admin users, permissions, and audit logs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPasswordDialogOpen(true)}>
            <Key className="w-4 h-4 mr-2" />
            Change My Password
          </Button>
          {isPrimaryAdmin && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#595959] hover:bg-[#404040]">
                  <Plus className="w-4 h-4 mr-2" /> Add Admin
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingAdmin ? 'Edit Admin' : 'Create New Admin'}</DialogTitle>
                  <DialogDescription>
                    {editingAdmin ? 'Update admin details' : 'Add a new admin user to the system'}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="admin@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                  {!editingAdmin && (
                    <div className="space-y-2">
                      <Label>Password *</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Minimum 6 characters"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSubmit} className="bg-[#595959] hover:bg-[#404040]">
                    {editingAdmin ? 'Update Admin' : 'Create Admin'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="admins" className="space-y-4">
        <TabsList>
          <TabsTrigger value="admins" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Admin Users
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Audit Logs
          </TabsTrigger>
        </TabsList>

        {/* Admin Users Tab */}
        <TabsContent value="admins">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Admin Users</CardTitle>
                  <CardDescription>{admins.length} admin user(s)</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : admins.length === 0 ? (
                <div className="text-center py-12">
                  <UserCog className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No admin users found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {admins.map(admin => (
                    <Card key={admin.id} className={`border ${admin.status === 'disabled' ? 'bg-gray-50 opacity-75' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              admin.is_primary_admin ? 'bg-purple-100' : 'bg-gray-200'
                            }`}>
                              {admin.is_primary_admin ? (
                                <ShieldCheck className="w-6 h-6 text-purple-600" />
                              ) : (
                                <Shield className="w-6 h-6 text-[#595959]" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-900">{admin.name}</h3>
                                {admin.is_primary_admin && (
                                  <Badge className="bg-purple-100 text-purple-800">Primary Admin</Badge>
                                )}
                                <Badge className={admin.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                  {admin.status === 'active' ? 'Active' : 'Disabled'}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-500">{admin.email}</p>
                              {admin.phone && <p className="text-sm text-gray-400">{admin.phone}</p>}
                              <p className="text-xs text-gray-400 mt-1">
                                ID: {admin.id.slice(0, 8)}... | Role: {admin.role}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Permissions count */}
                            <Badge variant="outline" className="text-xs">
                              {admin.is_primary_admin ? 'All' : (admin.permissions?.length || 0)} permissions
                            </Badge>

                            {/* Actions - Only for non-primary admins and if current user is primary */}
                            {isPrimaryAdmin && !admin.is_primary_admin && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openPermissionsDialog(admin)}
                                  title="Manage Permissions"
                                >
                                  <Key className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditDialog(admin)}
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => toggleStatus(admin)}
                                  title={admin.status === 'active' ? 'Deactivate' : 'Activate'}
                                  className={admin.status === 'active' ? 'text-orange-600' : 'text-green-600'}
                                >
                                  {admin.status === 'active' ? (
                                    <ShieldX className="w-4 h-4" />
                                  ) : (
                                    <ShieldCheck className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(admin)}
                                  className="text-red-600 hover:text-red-700"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>Audit Logs</CardTitle>
              <CardDescription>Track all admin-related changes</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No audit logs yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map(log => (
                    <div key={log.id} className="flex items-start gap-4 p-3 border rounded-lg hover:bg-gray-50">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        log.action.includes('created') ? 'bg-green-100' :
                        log.action.includes('deleted') ? 'bg-red-100' :
                        log.action.includes('deactivated') ? 'bg-orange-100' :
                        'bg-blue-100'
                      }`}>
                        {log.action.includes('created') && <Plus className="w-5 h-5 text-green-600" />}
                        {log.action.includes('deleted') && <Trash2 className="w-5 h-5 text-red-600" />}
                        {log.action.includes('updated') && <Edit className="w-5 h-5 text-blue-600" />}
                        {log.action.includes('activated') && <CheckCircle className="w-5 h-5 text-green-600" />}
                        {log.action.includes('deactivated') && <XCircle className="w-5 h-5 text-orange-600" />}
                        {log.action.includes('permissions') && <Key className="w-5 h-5 text-purple-600" />}
                        {log.action.includes('password') && <Lock className="w-5 h-5 text-gray-600" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline">{getActionLabel(log.action)}</Badge>
                          <span className="text-sm text-gray-900 font-medium">{log.target_user_email}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          By: {log.performed_by_email}
                        </p>
                        {log.changes && (
                          <p className="text-xs text-gray-400 mt-1">
                            Changes: {JSON.stringify(log.changes).slice(0, 100)}...
                          </p>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">
                        {formatDate(log.timestamp)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Permissions Dialog */}
      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Permissions</DialogTitle>
            <DialogDescription>
              Configure permissions for {selectedAdmin?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllPermissions}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={clearAllPermissions}>
                Clear All
              </Button>
            </div>

            {Object.entries(groupPermissionsByCategory()).map(([category, perms]) => (
              <div key={category} className="space-y-2">
                <h4 className="font-medium text-gray-700 flex items-center gap-2">
                  <Badge className={PERMISSION_CATEGORIES[category]?.color || 'bg-gray-100'}>
                    {PERMISSION_CATEGORIES[category]?.label || category}
                  </Badge>
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {perms.map(perm => (
                    <div key={perm.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={perm.value}
                        checked={formData.permissions.includes(perm.value)}
                        onCheckedChange={() => togglePermission(perm.value)}
                      />
                      <Label htmlFor={perm.value} className="text-sm cursor-pointer">
                        {perm.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePermissionUpdate} className="bg-[#595959] hover:bg-[#404040]">
              Save Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Enter current password"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Minimum 6 characters"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Re-enter new password"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePasswordChange} className="bg-[#595959] hover:bg-[#404040]">
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
